import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, signIn as supabaseSignIn, signUp as supabaseSignUp, signOut as supabaseSignOut, resetPassword as supabaseResetPassword } from '@wammasys/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Initialize auth state from Supabase session
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        // Get the current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        toast.error('Authentication system could not be initialized');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: string, newSession: Session | null) => {
      if (newSession) {
        // Check if email is verified
        if (!newSession.user.email_confirmed_at) {
          console.log('Unverified user session detected, signing out');
          // Sign out unverified users
          await supabase.auth.signOut();
          toast.error('Email verification required. Please check your inbox for the verification link.');
          setSession(null);
          setUser(null);
          setIsAuthenticated(false);
        } else {
          // Only set authenticated state for verified users
          setSession(newSession);
          setUser(newSession.user);
          setIsAuthenticated(true);
        }
      } else {
        setSession(null);
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    // Clean up subscription on unmount
    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabaseSignUp(email, password);
      
      if (error) throw error;
      
      toast.success('Registration successful! Please check your email to confirm your account.');
      return data;
    } catch (error: any) {
      console.error('Error signing up:', error);
      toast.error(error.message || 'Failed to sign up');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabaseSignIn(email, password);
      
      if (error) throw error;
      
      if (data.session) {
        // Check if email is verified
        if (!data.user.email_confirmed_at) {
          // Sign out immediately to prevent access
          await supabase.auth.signOut();
          // Throw the error instead of returning it
          throw new Error('Email not verified. Please check your inbox for the verification link.');
        }
        
        setSession(data.session);
        setUser(data.user);
        setIsAuthenticated(true);
        toast.success('Logged in successfully');
        navigate('/dashboard');
      }
      return data;
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast.error(error.message || 'Failed to sign in');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabaseSignOut();
      
      if (error) throw error;
      
      setSession(null);
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
      navigate('/auth/login');
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast.error(error.message || 'Failed to sign out');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    setLoading(true);
    try {
      const { error } = await supabaseResetPassword(email);
      
      if (error) throw error;
      
      toast.success('Password reset email sent');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast.error(error.message || 'Failed to send reset email');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
